import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
import { MAGPIE_CLIENT_LOGGER_NAME, MetricsReporter } from '../config';
import { ConnectionError } from '../errors';
import { Level, Logger } from 'simple-logging-lib';
import { MagpieDebugHandler, MagpieErrorHandler } from '../logging';
import { MagpieConfigManager, Topic, fromConnectionSpec } from '../models';
import { getDeferred, safeCallback } from '../utils';
import { ConnectionStateManager, ConnectionStatus } from './connection-status';
const defaultUseTopicOptions = {
  subscribe: true
};
var _topics = /*#__PURE__*/_classPrivateFieldLooseKey("topics");
export class MagpieConnection {
  /**
   * `#topics` is a WeakMap that stores a mapping of `TopicDefinition` to
   * `Topic` instances. This is used to cache `Topic` instances and reuse them
   * for subsequent calls to `useTopic`.
   * This is useful for scenarios where multiple components need to interact
   * with the same topic. `WeakMap` is used to avoid memory leaks when the
   * `TopicDefinition` is garbage collected.
   */

  constructor(spec = {}) {
    this.logger = Logger.getLogger(MAGPIE_CLIENT_LOGGER_NAME);
    this.connectionStrategy = null;
    this.proxy = getDeferred();
    Object.defineProperty(this, _topics, {
      writable: true,
      value: new WeakMap()
    });
    this.setupLogger = () => {
      const debug = this.configManager.get('debug');
      let debugAll = false;
      try {
        debugAll = localStorage.getItem('MAGPIE_DEBUG_ALL') === 'true';
      } catch (error) {
        /* NO-OP */
      }
      const loggingLevel = debug || debugAll ? Level.DEBUG : Level.ERROR;
      this.logger.setLevel(loggingLevel);
      this.logger.addHandler(new MagpieDebugHandler(this.clientMetadata));
      this.logger.addHandler(new MagpieErrorHandler(this.clientMetadata));
    };
    this.connect = () => {
      const onError = this.configManager.get('onConnectionError');
      const connectionState = ConnectionStateManager.getInstance();
      if (!connectionState.canConnect()) {
        return;
      }
      connectionState.setStatus(ConnectionStatus.CONNECTING);
      const strategy = this.configManager.get('strategy');
      const debug = this.configManager.get('debug');
      const safeOnError = onError && safeCallback(onError);
      const connectionStrategy = strategy || new MagpieConfigManager.DefaultConnectionStrategy({
        clientMetadata: this.clientMetadata,
        debug
      });
      this.connectionStrategy = connectionStrategy;
      this.connectionStrategy.open().then(proxy => {
        this.proxy.resolve(proxy);
        this.logger.debug('Connection opened');
        connectionState.setStatus(ConnectionStatus.CONNECTED);
        window.addEventListener('pagehide', this.onPageHide);
      }).catch(cause => {
        const status = ConnectionStatus.FAILED_TO_CONNECT;
        connectionState.setStatus(status);
        const error = new ConnectionError(ConnectionError.Reason.FAILED_TO_OPEN, {
          originalError: cause,
          messageArgs: [status]
        });
        this.logger.error(error);
        MetricsReporter.reportConnectionError(Object.assign({}, this.clientMetadata, {
          status
        }));
        if (safeOnError) {
          safeOnError(error);
        }
      });
    };
    this.onPageHide = event => {
      if (!event.persisted) {
        // The page is being unloaded, so we should disconnect
        this.disconnect();
      }
    };
    this.runDisconnect = async () => {
      const connectionState = ConnectionStateManager.getInstance();
      if (!connectionState.canDisconnect()) {
        return Promise.resolve();
      }
      connectionState.setStatus(ConnectionStatus.DISCONNECTING);
      if (this.connectionStrategy) {
        await this.connectionStrategy.close();
        this.logger.debug('Connection closed');
        MagpieConnection.connection = undefined;
        window.removeEventListener('pagehide', this.onPageHide);
        connectionState.setStatus(ConnectionStatus.DISCONNECTED);
      }
    };
    this.disconnect = () => {
      this.logger.debug('Disconnecting');
      this.runDisconnect().catch(error => {
        this.logger.error(`Failed to close connection.\nCause: ${error.message}`);
      });
    };
    this.useTopic = (topicDefinition, options = defaultUseTopicOptions) => {
      const {
        subscribe: shouldSubscribe
      } = Object.assign({}, defaultUseTopicOptions, options);
      if (_classPrivateFieldLooseBase(this, _topics)[_topics].has(topicDefinition)) {
        return _classPrivateFieldLooseBase(this, _topics)[_topics].get(topicDefinition);
      }
      const topic = new Topic(topicDefinition, this.proxy.promise, this.clientMetadata);
      _classPrivateFieldLooseBase(this, _topics)[_topics].set(topicDefinition, topic);
      if (shouldSubscribe) {
        topic.subscribe().then(() => {
          this.logger.debug(`Subscribed to topic ${topicDefinition.getName()}`);
        }).catch(e => {
          this.logger.error('Failed to subscribe to topic', e);
          setTimeout(() => {
            throw e;
          }, 0);
        });
      }
      return topic;
    };
    this.getClientId = () => {
      var _this$connectionStrat;
      return (_this$connectionStrat = this.connectionStrategy) === null || _this$connectionStrat === void 0 || (_this$connectionStrat = _this$connectionStrat.getHandshakeResult()) === null || _this$connectionStrat === void 0 ? void 0 : _this$connectionStrat.clientId;
    };
    this.getActiveTopicSubscriptions = () => {
      var _this$connectionStrat2, _this$connectionStrat3;
      return (_this$connectionStrat2 = (_this$connectionStrat3 = this.connectionStrategy) === null || _this$connectionStrat3 === void 0 ? void 0 : _this$connectionStrat3.getActiveTopicSubscriptions()) !== null && _this$connectionStrat2 !== void 0 ? _this$connectionStrat2 : Promise.resolve([]);
    };
    this.onTopicSubscriptionsChange = onChange => {
      if (!this.connectionStrategy) {
        const connectionState = ConnectionStateManager.getInstance();
        const status = connectionState.getStatus();
        throw new ConnectionError(ConnectionError.Reason.UNINITIALIZED, {
          messageArgs: [status]
        });
      }
      return this.connectionStrategy.onTopicSubscriptionsChange(onChange);
    };
    this.setDebugMode = debugMode => {
      this.configManager.set('debug', debugMode);
      const loggingLevel = debugMode ? Level.DEBUG : Level.ERROR;
      this.logger.setLevel(loggingLevel);
    };
    this.spec = spec;
    // Get the config manager and provision it with the connection spec
    const overrides = fromConnectionSpec(spec);
    this.configManager = MagpieConfigManager.getInstance(overrides);

    // Lock the config manager to prevent further changes
    this.configManager.lock();
    this.clientMetadata = this.configManager.getClientMetadata();
    this.setupLogger();
  }

  /**
   * `MagpieConnection.create` is a factory method that returns a singleton
   * instance of `MagpieConnection`. It initializes the connection if it
   * doesn't exist. It's not required anymore, but it's kept for backwards
   * compatibility.
   */

  /**
   * `MagpieConnection.get` is a "get or create" method that returns a singleton
   * instance of `MagpieConnection`. It initializes the connection if it
   * doesn't exist.
   */

  /**
   * `useTopic` is a hook that returns a `Topic` instance for a given
   * `TopicDefinition`. It subscribes to the topic if `subscribe` is set to
   * `true` in the options (default behavior).
   * `Topic` instances are cached and reused for subsequent calls to `useTopic`.
   * `Topic` is a facade for interacting with a topic, including subscribing,
   * unsubscribing, and publishing messages.
   * The method will wait for the connection to be established before returning
   * the `Topic` instance. If the connection is not established within 5 seconds,
   * it will throw an error.
   */

  /**
   * Only for testing purposes
   */
}
MagpieConnection.create = (spec = {}) => {
  if (!MagpieConnection.connection) {
    const connection = new MagpieConnection(spec);
    connection.connect();
    MagpieConnection.connection = connection;
  }
  return MagpieConnection.connection;
};
MagpieConnection.get = () => {
  if (!MagpieConnection.connection) {
    const connection = new MagpieConnection();
    connection.connect();
    MagpieConnection.connection = connection;
  }
  return MagpieConnection.connection;
};
MagpieConnection.resetInstance = () => {
  var _MagpieConnection$con;
  ConnectionStateManager.getInstance().setStatus(ConnectionStatus.UNINITIALIZED);
  (_MagpieConnection$con = MagpieConnection.connection) === null || _MagpieConnection$con === void 0 || _MagpieConnection$con.disconnect();
  MagpieConnection.connection = undefined;
  MagpieConfigManager.resetInstance();
};